import type { RouteLocationRaw } from 'vue-router';

import type { IconObject } from '@/dsComponents/buttons/types';

import {
	type FollowingDropdownData,
	FollowingDropdownPopoverKind,
	FollowingDropdownTriggerMode,
	type FollowingSegment,
	FollowingSegmentType,
	type InterviewPopoverData,
} from '@/components/following/types';

interface LinkSegmentOptions {
	leftIcon?: string | IconObject;
	iconColor?: string;
	to: RouteLocationRaw;
}

interface ActionSegmentOptions {
	leftIcon?: string | IconObject;
	iconColor?: string;
	onClick: (event: MouseEvent) => void;
}

interface ReadonlySegmentOptions {
	leftIcon?: string | IconObject;
	iconColor?: string;
}

interface DropdownSegmentOptions {
	id?: string;
	leftIcon?: string | IconObject;
	iconColor?: string;
	mode?: FollowingDropdownTriggerMode;
	width?: string;
}

export const createTextSegment = (
	content: string,
	options: { multiline?: boolean } = {}
): FollowingSegment => ({
	type: FollowingSegmentType.TEXT,
	content,
	multiline: options.multiline,
});

export const createLinkSegment = (
	label: string,
	options: LinkSegmentOptions
): FollowingSegment => ({
	type: FollowingSegmentType.LINK,
	label,
	to: options.to,
	leftIcon: options.leftIcon,
	iconColor: options.iconColor,
});

export const createActionSegment = (
	label: string,
	options: ActionSegmentOptions
): FollowingSegment => ({
	type: FollowingSegmentType.ACTION,
	label,
	onClick: options.onClick,
	leftIcon: options.leftIcon,
	iconColor: options.iconColor,
});

export const createReadonlyLabelSegment = (
	label: string,
	options: ReadonlySegmentOptions = {}
): FollowingSegment => ({
	type: FollowingSegmentType.READONLY,
	label,
	leftIcon: options.leftIcon,
	iconColor: options.iconColor,
});

export const createStatusSegment = (
	label: string,
	color: string,
	icon = 'status-marker'
): FollowingSegment => ({
	type: FollowingSegmentType.STATUS,
	label,
	leftIcon: { icon, color },
});

export const createStatusDropdownSegment = (
	label: string,
	color: string,
	data: FollowingDropdownData,
	options: DropdownSegmentOptions = {},
	icon = 'status-marker'
): FollowingSegment => ({
	type: FollowingSegmentType.DROPDOWN,
	id: options.id,
	label,
	leftIcon: { icon, color },
	data,
	mode: options.mode ?? FollowingDropdownTriggerMode.HOVER,
	width: options.width,
});

export const createDropdownSegment = (
	label: string,
	data?: FollowingDropdownData,
	options: DropdownSegmentOptions = {}
): FollowingSegment => ({
	type: FollowingSegmentType.DROPDOWN,
	id: options.id,
	label,
	data,
	mode: options.mode,
	leftIcon: options.leftIcon,
	iconColor: options.iconColor,
	width: options.width,
});

export const createInterviewDropdownSegment = (
	label: string,
	data: InterviewPopoverData,
	options: DropdownSegmentOptions = {}
): FollowingSegment => ({
	type: FollowingSegmentType.DROPDOWN,
	id: options.id,
	label,
	data: { type: FollowingDropdownPopoverKind.INTERVIEW, payload: data },
	mode: FollowingDropdownTriggerMode.HOVER,
	leftIcon: options.leftIcon,
	iconColor: options.iconColor,
	width: options.width ?? '236px',
});
